import { useEffect, useState } from "react";

/*
    A page opened from a failed job's fix link says which row it is about:
    `?highlight=ad_group:42`. The row is outlined until the person either deals with it
    or has plainly seen it.

    It clears on the first touch of that row -- click, focus, keypress -- because at
    that point the outline has done its job and would only be in the way of the edit.
    Ten seconds is the backstop for a row nobody touches.

    The row is usually not in the DOM yet: budget, schedule and content all fetch
    first, so this waits for it rather than scrolling into an empty table.
*/
const VISIBLE_MS = 10000;
const WAIT_MS = 6000;
const POLL_MS = 100;

export function useArrivalHighlight(selector: string): string | null {
    const [token, setToken] = useState<string | null>(() => {
        try {
            return new URLSearchParams(window.location.search).get("highlight");
        } catch {
            // Some hosts render these views outside a browser location we can read.
            return null;
        }
    });

    useEffect(() => {
        if (!token) return;
        let fade: number | undefined;
        const started = Date.now();

        const clear = () => setToken(null);
        const onTouched = (event: Event) => {
            const row = document.querySelector(selector);
            if (row && event.target instanceof Node && row.contains(event.target)) clear();
        };
        // Capture phase: the row's own handlers may stop propagation on their way up.
        const events = ["pointerdown", "focusin", "keydown"] as const;
        events.forEach((name) => document.addEventListener(name, onTouched, true));

        const poll = window.setInterval(() => {
            const row = document.querySelector(selector);
            if (row) {
                window.clearInterval(poll);
                row.scrollIntoView({ block: "center", behavior: "smooth" });
                fade = window.setTimeout(clear, VISIBLE_MS);
            } else if (Date.now() - started > WAIT_MS) {
                // The row never arrived -- a stale link, or the data moved on.
                window.clearInterval(poll);
                clear();
            }
        }, POLL_MS);

        return () => {
            window.clearInterval(poll);
            if (fade) window.clearTimeout(fade);
            events.forEach((name) => document.removeEventListener(name, onTouched, true));
        };
    }, [token, selector]);

    return token;
}
